// src/App.js
import "./style.css";
import React, { useState, useEffect, useCallback, lazy, Suspense } from "react";
import { useTranslation } from "react-i18next";
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./AuthContext";
import { XPProvider } from "./XPContext";
import { JournalProvider, useJournal } from "./JournalContext";
import { FriendsProvider } from "./FriendsContext";
import AppLayout from "./AppLayout";
import { useJournalName } from "./JournalNameContext";
import useIsMobile, { useIsTablet } from "./useIsMobile";

// Code-split heavy / route-level components so they load on demand instead of
// shipping in the initial bundle. This keeps recharts, react-confetti, the
// sketch canvas and the large mobile app out of the first load.
const YearMonthSelector = lazy(() => import("./YearMonthSelector"));
const MemorableMoments = lazy(() => import("./MemorableMoments"));
const Dashboard = lazy(() => import("./Dashboard"));
const MainPageFooter = lazy(() => import("./MainPageFooter"));
const JournalEntryPage = lazy(() => import("./JournalEntryPage"));
const LandingPage = lazy(() => import("./LandingPage"));
const AuthPage = lazy(() => import("./AuthPage"));
const ResetPasswordPage = lazy(() => import("./ResetPasswordPage"));
const FriendsPage = lazy(() => import("./FriendsPage"));
const FavouriteMomentsPage = lazy(() => import("./FavouriteMomentsPage"));
const AllJournalEntriesPage = lazy(() => import("./AllJournalEntriesPage"));
const SketchGalleryPage = lazy(() => import("./SketchGalleryPage"));
const InsightsPanel = lazy(() => import("./InsightsPanel"));
const MoodYearMatrix = lazy(() => import("./MoodYearMatrix"));
const JournalMapPage = lazy(() => import("./JournalMapPage"));
const MobileApp = lazy(() => import("./MobileApp"));
const TabletApp = lazy(() => import("./TabletApp"));
const PrivacyPage = lazy(() => import("./PrivacyPage"));

const LoadingFallback = () => (
  <div className="loading-screen">
    <img
      src={`${process.env.PUBLIC_URL || ""}/ihkeely-logo.png`}
      alt="ihkeely"
      className="loading-brand-logo"
    />
    <p>Loading...</p>
  </div>
);

const JOURNAL_VIEWS = [
  { id: "journal", tKey: "journalTab", icon: "book" },
  { id: "dashboard", tKey: "dashboard", icon: "dashboard" },
  { id: "insights", tKey: "insightsTrends", icon: "insights" },
  { id: "moodMatrix", tKey: "moodMatrixTitle", icon: "grid_view" },
];

const journalViewPath = (view) => (view && view !== "journal" ? `/journal?view=${view}` : "/journal");

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading, emailVerified } = useAuth();

  if (loading) {
    return <LoadingFallback />;
  }

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  if (!emailVerified) {
    return <Navigate to="/verify-email" replace />;
  }

  return children;
};

// Redirect logged-in users from landing to journal
const LandingOrRedirect = ({ onEnter }) => {
  const { user, loading, emailVerified } = useAuth();
  if (loading) {
    return <LoadingFallback />;
  }
  if (user && emailVerified) {
    return <Navigate to="/journal" replace />;
  }
  return <LandingPage onEnter={onEnter} />;
};

// Redirect logged-in users from auth page to journal
const AuthOrRedirect = () => {
  const { user, loading, emailVerified } = useAuth();
  if (loading) {
    return <LoadingFallback />;
  }
  if (user && emailVerified) {
    return <Navigate to="/journal" replace />;
  }
  return <AuthPage />;
};

// Email Verification Pending Screen
const VerifyEmailPage = () => {
  const { user, loading, emailVerified, signOut } = useAuth();

  if (loading) {
    return <LoadingFallback />;
  }

  if (!user) return <Navigate to="/auth" replace />;
  if (emailVerified) return <Navigate to="/journal" replace />;

  return (
    <div className="auth-page">
      <div className="auth-container">
        <div className="auth-header">
          <div className="auth-logo">✉</div>
          <h1 className="auth-title">Verify Your Email</h1>
          <p className="auth-subtitle">
            We sent a verification link to <strong>{user.email}</strong>.
            Please check your inbox and click the link to continue.
          </p>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginTop: '1.5rem' }}>
          <button
            className="auth-button"
            onClick={() => window.location.reload()}
          >
            I've verified my email
          </button>
          <button
            className="auth-link"
            onClick={async () => { await signOut(); window.location.href = '/auth'; }}
          >
            Sign out and use a different account
          </button>
        </div>
      </div>
    </div>
  );
};

// Main Journal App Component (content only - header is in AppLayout)
const JournalApp = ({ isDarkMode, toggleTheme }) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();
  const { journalName, openRenameModal } = useJournalName();
  const { setHabitsMonth } = useJournal();
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const navigate = useNavigate();
  const location = useLocation();
  const viewParam = new URLSearchParams(location.search).get("view");
  const mainView = JOURNAL_VIEWS.some((v) => v.id === viewParam)
    ? viewParam
    : "journal";

  const handleMatrixOpenEntry = useCallback((dateKey) => {
    const [y, m, d] = dateKey.split("-");
    navigate(`/entry/${y}/${m}/${d}`);
  }, [navigate]);

  const openTodayEntry = useCallback(() => {
    const n = new Date();
    navigate(`/entry/${n.getFullYear()}/${n.getMonth() + 1}/${n.getDate()}`);
  }, [navigate]);

  useEffect(() => {
    setHabitsMonth(selectedYear, selectedMonth);
  }, [selectedYear, selectedMonth, setHabitsMonth]);

  useEffect(() => {
    if (location.hash !== "#habits") return;
    const t = window.setTimeout(() => {
      document.getElementById("habits")?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 80);
    return () => window.clearTimeout(t);
  }, [location.hash, mainView]);

  if (isMobile) {
    return (
      <Suspense fallback={<LoadingFallback />}>
        <MobileApp
          isDarkMode={isDarkMode}
          toggleTheme={toggleTheme}
          journalName={journalName}
          onRenameJournal={openRenameModal}
        />
      </Suspense>
    );
  }

  if (isTablet) {
    return (
      <Suspense fallback={<LoadingFallback />}>
        <TabletApp
          isDarkMode={isDarkMode}
          toggleTheme={toggleTheme}
          journalName={journalName}
          onRenameJournal={openRenameModal}
        />
      </Suspense>
    );
  }

  return (
    <Suspense fallback={<LoadingFallback />}>
      <nav className="journal-view-tabs" aria-label={t("navigation")}>
        {JOURNAL_VIEWS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            className={`journal-view-tab ${mainView === tab.id ? "is-active" : ""}`}
            onClick={() => navigate(journalViewPath(tab.id))}
            aria-current={mainView === tab.id ? "page" : undefined}
          >
            <span className="material-symbols-outlined">{tab.icon}</span>
            <span>{t(tab.tKey)}</span>
          </button>
        ))}
      </nav>
      {mainView !== "moodMatrix" && (
        <YearMonthSelector
          selectedYear={selectedYear}
          setSelectedYear={setSelectedYear}
          selectedMonth={selectedMonth}
          setSelectedMonth={setSelectedMonth}
          onEditSettings={openRenameModal}
        />
      )}
      {mainView === "trackers" && (
        <MemorableMoments
          year={selectedYear}
          month={selectedMonth}
          useNewDesign
          variant="trackers"
        />
      )}
      {mainView === "journal" && (
        <MemorableMoments
          year={selectedYear}
          month={selectedMonth}
          useNewDesign
          variant="full"
        />
      )}
      {mainView === "dashboard" && (
        <Dashboard year={selectedYear} month={selectedMonth} />
      )}
      {mainView === "insights" && (
        <InsightsPanel year={selectedYear} month={selectedMonth} />
      )}
      {mainView === "moodMatrix" && (
        <MoodYearMatrix onOpenEntry={handleMatrixOpenEntry} />
      )}
      {(mainView === "journal" || mainView === "trackers") && (
        <>
          {mainView === "journal" && (
            <MainPageFooter year={selectedYear} month={selectedMonth} />
          )}
          <button
            type="button"
            className="main-page-fab"
            aria-label={t("writeToday")}
            title={t("writeToday")}
            onClick={openTodayEntry}
          >
            <span className="material-symbols-outlined">edit_note</span>
          </button>
        </>
      )}
    </Suspense>
  );
};

const AppContent = () => {
  const location = useLocation();
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const saved = localStorage.getItem("theme");
    return saved === "dark";
  });

  useEffect(() => {
    const path = location.pathname;
    const isPublicAuth = path === "/" || path === "/auth" || path === "/reset-password" || path === "/verify-email" || path === "/privacy";

    if (isPublicAuth) {
      document.body.classList.remove("dark-mode", "light-mode");
    } else {
      if (isDarkMode) {
        document.body.classList.add("dark-mode");
        document.body.classList.remove("light-mode");
        localStorage.setItem("theme", "dark");
      } else {
        document.body.classList.add("light-mode");
        document.body.classList.remove("dark-mode");
        localStorage.setItem("theme", "light");
      }
    }
  }, [isDarkMode, location.pathname]);

  const toggleTheme = () => {
    setIsDarkMode(prev => !prev);
  };

  const handleEnterJournal = () => {
    // Apply theme when entering journal
    if (isDarkMode) {
      document.body.classList.add("dark-mode");
      document.body.classList.remove("light-mode");
    } else {
      document.body.classList.add("light-mode");
      document.body.classList.remove("dark-mode");
    }
  };

  return (
    <XPProvider>
      <JournalProvider>
        <FriendsProvider>
        <Suspense fallback={<LoadingFallback />}>
        <Routes>
          <Route
            path="/"
            element={<LandingOrRedirect onEnter={handleEnterJournal} />}
          />
          <Route
            path="/privacy"
            element={<PrivacyPage />}
          />
          <Route
            path="/auth"
            element={<AuthOrRedirect />}
          />
          <Route
            path="/reset-password"
            element={<ResetPasswordPage />}
          />
          <Route
            path="/verify-email"
            element={<VerifyEmailPage />}
          />
          <Route
            path="/journal"
            element={
              <ProtectedRoute>
                <AppLayout isDarkMode={isDarkMode} toggleTheme={toggleTheme}>
                  <JournalApp isDarkMode={isDarkMode} toggleTheme={toggleTheme} />
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/friends"
            element={
              <ProtectedRoute>
                <AppLayout isDarkMode={isDarkMode} toggleTheme={toggleTheme}>
                  <div className="entry-route-wrap friends-social-route">
                    <FriendsPage isDarkMode={isDarkMode} />
                  </div>
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/favourites"
            element={
              <ProtectedRoute>
                <AppLayout isDarkMode={isDarkMode} toggleTheme={toggleTheme}>
                  <div className="entry-route-wrap">
                    <FavouriteMomentsPage />
                  </div>
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/entries"
            element={
              <ProtectedRoute>
                <AppLayout isDarkMode={isDarkMode} toggleTheme={toggleTheme}>
                  <div className="entry-route-wrap">
                    <AllJournalEntriesPage />
                  </div>
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/sketches"
            element={
              <ProtectedRoute>
                <AppLayout isDarkMode={isDarkMode} toggleTheme={toggleTheme}>
                  <div className="entry-route-wrap">
                    <SketchGalleryPage isMobile={false} />
                  </div>
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/map"
            element={
              <ProtectedRoute>
                <AppLayout isDarkMode={isDarkMode} toggleTheme={toggleTheme}>
                  <div className="entry-route-wrap">
                    <JournalMapPage />
                  </div>
                </AppLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/entry/:year/:month/:day"
            element={
              <ProtectedRoute>
                <AppLayout isDarkMode={isDarkMode} toggleTheme={toggleTheme}>
                  <div className="entry-route-wrap">
                    <JournalEntryPage />
                  </div>
                </AppLayout>
              </ProtectedRoute>
            }
          />
        </Routes>
        </Suspense>
        </FriendsProvider>
      </JournalProvider>
    </XPProvider>
  );
};

const App = () => {
  return (
    <AuthProvider>
      <Router>
        <AppContent />
      </Router>
    </AuthProvider>
  );
};

export default App;
